'use client';

import { useEffect, useRef } from 'react';

import useTabActive from '../../../hooks/useTabActive.js';

type Options = {
  timeout: number;
  onInactive: () => void;
  onReset?: () => void;
  resetThrottle?: number;
};

export function useInactivity({ timeout, onInactive, onReset, resetThrottle = 0 }: Options) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(0);
  const callbackRef = useRef(onInactive);
  const resetRef = useRef(onReset);
  const lastResetRef = useRef(0);

  // avoid stale closures
  useEffect(() => {
    callbackRef.current = onInactive;
    resetRef.current = onReset;
  });

  const triggerIfExpired = () => {
    if (Date.now() - lastActivityRef.current >= timeout) {
      callbackRef.current();
    }
  };

  useTabActive(triggerIfExpired);

  useEffect(() => {
    const throttledOnReset = () => {
      if (lastActivityRef.current - lastResetRef.current < resetThrottle) {
        return;
      }
      lastResetRef.current = lastActivityRef.current;
      resetRef?.current?.();
    };
    const resetTimer = () => {
      lastActivityRef.current = Date.now();
      throttledOnReset();
      clearTimeout(timeoutRef.current!);

      timeoutRef.current = setTimeout(() => {
        callbackRef.current();
      }, timeout);
    };

    const events: (keyof WindowEventMap)[] = ['keydown', 'click', 'scroll', 'touchstart'];

    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));

    resetTimer();

    return () => {
      clearTimeout(timeoutRef.current!);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [timeout, resetThrottle]);
}
